// src/shared/ModalContext/ModalProvider.js

import React, { useState, useCallback } from 'react';
import ModalContext from './ModalContext';

import { ArchiveConfirmationDialog, UnarchiveConfirmationDialog, CreatePotentialCandidateDialog, ViewCandidateDialog } from '../../components/IdealCandidateView/IdealCandidateView';
import CandidateComparisonModal from '../../components/IdealCandidateView/components/ComparisonView/CandidateComparisonModal';
import ScheduleInterviewModal from '../../components/IdealCandidateView/components/ScheduleInterviewModal';
import UpgradeModal from '../../components/App/Requirements/upgradeModal';
import SubscriptionModal from '../../components/App/Requirements/SubModal';
import VerificationModal from '../../components/App/Requirements/EmailVerificationModal';
import { ForgotPasswordModal } from '../../components/Authentication/SignInForm/ForgotPasswordModal';
import { PersonalAccessTokensModal } from '../../components/PersonalAccessTokens/PersonalAccessTokensModal';
import { InfoDialog, SuccessDialog } from '../../components/Help/HelpPage';
import { CalendarModal } from '../../components/Calendar/CalendarModal';
import MeetingDetailsModal from '../../components/Calendar/MeetingDetailsModal';
import { Dialog, Box, Typography, Button, Avatar, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const ScheduleInterviewSuccessModal = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: '440px' } }}>
    <Box sx={{ p: 3, pt: 4, textAlign: 'center' }}>
        <Avatar sx={{ bgcolor: 'success.light', color: 'success.main', width: 56, height: 56, mx: 'auto', mb: 2 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 28 }} />
        </Avatar>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
            Meeting Scheduled!
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            The interview has been successfully scheduled. An invitation has been sent to the candidate.
        </Typography>
        <Button onClick={onClose} variant="contained" fullWidth sx={{
            bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, py: 1.5,
            borderRadius: '8px', textTransform: 'none', fontSize: '1rem'
          }}
        >
          Done
        </Button>
    </Box>
  </Dialog>
);

const EmailMismatchWarningModal = ({ open, onClose, props }) => {
    const { onConfirm, userEmail, googleEmail } = props;
    return (
        <Dialog open={open} onClose={() => onClose(false)} PaperProps={{ sx: { borderRadius: 4, maxWidth: '480px' } }}>
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Avatar sx={{ bgcolor: 'warning.light', color: 'warning.main', width: 56, height: 56, mx: 'auto', mb: 2 }}>
                    <WarningAmberIcon sx={{ fontSize: 28 }} />
                </Avatar>
                <DialogTitle sx={{ p: 0, pb: 1, fontWeight: 600 }}>Email Mismatch</DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <Typography color="text.secondary">
                        You are attempting to link the Google account for **{googleEmail}**, which is different from your Candidate Tools account email (**{userEmail}**).
                        <br /><br />
                        Do you want to continue?
                    </Typography>
                </DialogContent>
            </Box>
            <DialogActions sx={{ justifyContent: 'center', p: 2, pt: 1 }}>
                <Button onClick={() => onClose(false)} variant="outlined" sx={{ borderRadius: '20px', px: 3 }}>Cancel</Button>
                <Button onClick={() => { onConfirm(); onClose(true); }} variant="contained" color="warning" sx={{ borderRadius: '20px', px: 3 }}>Continue</Button>
            </DialogActions>
        </Dialog>
    );
};


const MODAL_COMPONENTS = {
  // IdealCandidateView Modals
  ARCHIVE_CONFIRMATION: ArchiveConfirmationDialog,
  UNARCHIVE_CONFIRMATION: UnarchiveConfirmationDialog,
  CREATE_POTENTIAL_CANDIDATE: CreatePotentialCandidateDialog,
  VIEW_CANDIDATE_DETAILS: ViewCandidateDialog,
  CANDIDATE_COMPARISON: CandidateComparisonModal,
  SCHEDULE_INTERVIEW: ScheduleInterviewModal,
  MEETING_CREATION_SUCCESS: ScheduleInterviewSuccessModal,

  // App Requirement Modals
  UPGRADE_MODAL: UpgradeModal,
  SUBSCRIPTION_MODAL: SubscriptionModal,
  VERIFICATION_MODAL: VerificationModal,

  // Authentication Modals
  FORGOT_PASSWORD: ForgotPasswordModal,

  // PAT Modals
  PAT_NOTIFICATION: PersonalAccessTokensModal,

  // Help Page Modals
  HELP_INFO: InfoDialog,
  HELP_SUCCESS: SuccessDialog,
  CALENDAR_MODAL: CalendarModal,
  EMAIL_MISMATCH_WARNING: EmailMismatchWarningModal,
  MEETING_DETAILS: MeetingDetailsModal,
};

const ModalProvider = ({ children }) => {
  const [modal, setModal] = useState({ type: null, props: {}, resolve: null });

  const openModal = useCallback((type, props = {}) => {
    return new Promise((resolve) => {
      console.log(`Opening modal: ${type}`, props);
      setModal({ type, props, resolve });
    });
  }, []);

  const closeModal = useCallback((value) => {
    if (modal.resolve) {
      modal.resolve(value);
    }
    console.log('Closing modal');
    setModal({ type: null, props: {}, resolve: null });
  }, [modal]);

  const renderModal = () => {
    const ModalComponent = MODAL_COMPONENTS[modal.type];
    if (!ModalComponent) {
      return null;
    }
    return <ModalComponent {...modal.props} open={true} onClose={closeModal} props={modal.props} />;
  };

  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      {renderModal()}
    </ModalContext.Provider>
  );
};

export default ModalProvider;